/**
 * Bottom tab bar для админки: Главная | Заказы | Платежи | События.
 */

import { NavLink } from "react-router-dom";
import { useAuth } from "../../lib/auth.tsx";
import {
  getVisibleAdminNavItems,
  type AdminNavItem,
  type AdminNavIcon,
} from "../../config/admin-nav.ts";
import {
  IconReceipt,
  IconReceiptSolid,
  IconClipboardList,
  IconClipboardListSolid,
  IconCalendar,
  IconCalendarSolid,
  IconTicket,
  IconHome,
  IconHomeSolid,
  IconHomeHeart,
  IconUsers,
  IconMegaphone,
  IconNewspaper,
  IconChartBar,
  IconCog,
} from "../Icons.tsx";

/** Map icon names to components (outline) */
const ADMIN_NAV_ICONS: Record<
  AdminNavIcon,
  React.ComponentType<{ className?: string }>
> = {
  home: IconHome,
  payments: IconReceipt,
  orders: IconClipboardList,
  events: IconCalendar,
  tickets: IconTicket,
  homeGroups: IconHomeHeart,
  ministries: IconUsers,
  broadcasts: IconMegaphone,
  content: IconNewspaper,
  analytics: IconChartBar,
  settings: IconCog,
};

/** Map icon names to solid components (active state) */
const ADMIN_NAV_ICONS_SOLID: Partial<
  Record<AdminNavIcon, React.ComponentType<{ className?: string }>>
> = {
  home: IconHomeSolid,
  payments: IconReceiptSolid,
  orders: IconClipboardListSolid,
  events: IconCalendarSolid,
};

interface AdminTab {
  id: string;
  path: string;
  pathEnd: boolean;
  label: string;
  icon: AdminNavItem["icon"];
}

function buildAdminTabs(items: AdminNavItem[]): AdminTab[] {
  return items.map((item) => ({
    id: item.id,
    path: item.path,
    pathEnd: item.pathEnd ?? true,
    label: item.label,
    icon: item.icon,
  }));
}

const iconClass = "w-5 h-5 xs:w-5 xs:h-5 md-mobile:w-6 md-mobile:h-6 lg-mobile:w-6 lg-mobile:h-6 flex-shrink-0";

function TabIcon({ tab, isActive }: { tab: AdminTab; isActive: boolean }) {
  const SolidIcon = ADMIN_NAV_ICONS_SOLID[tab.icon];
  const Icon = isActive && SolidIcon ? SolidIcon : ADMIN_NAV_ICONS[tab.icon];
  return <Icon className={iconClass} />;
}

export function AdminTabBar() {
  const { account } = useAuth();
  const visible = getVisibleAdminNavItems(
    account ? { permissions: account.permissions } : null,
  );
  const tabs = buildAdminTabs(visible);

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 safe-area-bottom border-t border-slate-200/80 bg-amber-50/90 backdrop-blur-md lg:hidden"
      role="tablist"
      aria-label="Разделы админки"
    >
      <div className="page-container">
        <div className="flex items-center h-[44px] xs:h-[46px] md-mobile:h-[48px] lg-mobile:h-[50px] pt-1 pb-1">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className="flex-1 min-w-0 flex items-center justify-center min-h-0"
            >
              <NavLink
                to={tab.path}
                end={tab.pathEnd}
                className={({ isActive }) =>
                  `tab-item ${isActive ? "tab-item-active" : ""}`
                }
                role="tab"
              >
                {({ isActive }) => (
                  <>
                    <TabIcon tab={tab} isActive={isActive} />
                    <span>{tab.label}</span>
                  </>
                )}
              </NavLink>
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
}
